/**
 * Fit Text — Level 2 Alpine component
 *
 * Shrinks the element's font-size so its single-line text fits within the
 * parent's content width. Used by the contact_links block so the
 * editorial-scale link labels (e.g. a long email address) never wrap
 * regardless of viewport, while short labels keep their natural CSS size.
 *
 * Pairs with `whitespace-nowrap` on the element. Waits for web fonts so the
 * measurement uses real glyph metrics, then refits whenever the parent
 * resizes.
 *
 * Group mode: when the element has a `data-fit-group="<name>"` attribute,
 * all elements in the same group share a single font-size — the smallest
 * value needed for any group member to fit on one line. Use this to keep
 * sibling labels visually consistent (e.g. the LinkedIn / Instagram pair).
 */

const groups = new Map();

export default function fitText() {
    return {
        observer: null,
        groupName: null,

        init() {
            this.groupName = this.$el.dataset.fitGroup || null;
            if (this.groupName) {
                if (!groups.has(this.groupName)) {
                    groups.set(this.groupName, new Set());
                }
                groups.get(this.groupName).add(this.$el);
            }

            const start = () => {
                if (!document.contains(this.$el)) return;
                if ('ResizeObserver' in window) {
                    this.observer = new ResizeObserver(() => this.fit());
                    this.observer.observe(this.$el.parentElement);
                } else {
                    this.fit();
                }
            };

            if (document.fonts?.ready) {
                document.fonts.ready.then(start);
            } else {
                start();
            }
        },

        destroy() {
            this.observer?.disconnect();
            this.observer = null;
            if (this.groupName && groups.has(this.groupName)) {
                const set = groups.get(this.groupName);
                set.delete(this.$el);
                if (set.size === 0) groups.delete(this.groupName);
            }
        },

        fit() {
            if (this.groupName && groups.has(this.groupName)) {
                fitGroup(this.groupName);
            } else {
                fitElement(this.$el);
            }
        },
    };
}

function fitElement(el) {
    const parent = el.parentElement;
    if (!parent) return;
    el.style.fontSize = '';
    const target = computeTargetSize(el, parent);
    if (target !== null) el.style.fontSize = `${target}px`;
}

function fitGroup(name) {
    const members = Array.from(groups.get(name) || []).filter((el) => document.contains(el));
    if (!members.length) return;

    members.forEach((el) => {
        el.style.fontSize = '';
    });

    let minSize = Infinity;
    let cssDefault = Infinity;

    members.forEach((el) => {
        const parent = el.parentElement;
        if (!parent) return;
        const css = parseFloat(window.getComputedStyle(el).fontSize);
        if (css < cssDefault) cssDefault = css;
        const target = computeTargetSize(el, parent);
        const usable = target !== null ? target : css;
        if (usable < minSize) minSize = usable;
    });

    if (minSize < cssDefault) {
        const size = `${minSize}px`;
        members.forEach((el) => {
            el.style.fontSize = size;
        });
    }
}

function computeTargetSize(el, parent) {
    const parentStyle = window.getComputedStyle(parent);
    const padX = parseFloat(parentStyle.paddingLeft) + parseFloat(parentStyle.paddingRight);
    const available = parent.clientWidth - padX;
    const textWidth = el.scrollWidth;
    if (available <= 0 || textWidth <= available) return null;
    const currentSize = parseFloat(window.getComputedStyle(el).fontSize);
    return currentSize * (available / textWidth) * 0.99;
}
